import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import ParsePreview from '../components/ParsePreview.jsx';
import client from '../api/client.js';

const styles = {
  container: {
    padding: '16px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid #F4956A',
    fontSize: '15px',
    backgroundColor: '#FFFFFF',
    color: '#2D2424',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid #F4956A',
    fontSize: '15px',
    backgroundColor: '#FFFFFF',
    color: '#2D2424',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid #F4956A',
    fontSize: '15px',
    backgroundColor: '#FFFFFF',
    color: '#2D2424',
    outline: 'none',
    boxSizing: 'border-box',
    appearance: 'none',
  },
  previewSection: {
    marginBottom: '16px',
  },
  submitBtn: {
    display: 'block',
    width: '100%',
    padding: '14px',
    backgroundColor: 'var(--tg-theme-button-color, #007aff)',
    color: 'var(--tg-theme-button-text-color, #fff)',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  errorText: {
    color: '#ff3b30',
    fontSize: '13px',
    marginTop: '4px',
  },
  successMsg: {
    backgroundColor: '#34c75922',
    border: '1px solid #34c759',
    borderRadius: '10px',
    padding: '12px',
    fontSize: '14px',
    color: '#34c759',
    marginBottom: '16px',
    textAlign: 'center',
  },
  duplicateWarning: {
    backgroundColor: '#ff9f0a22',
    border: '1px solid #ff9f0a',
    borderRadius: '10px',
    padding: '12px',
    fontSize: '14px',
    color: '#ff9f0a',
    marginBottom: '16px',
  },
};

export default function AddItem() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialUrl = searchParams.get('url') || '';
  const initialListId = searchParams.get('list_id') || '';
  const initialCategoryId = searchParams.get('category_id') || '';

  const [url, setUrl] = useState(initialUrl);
  const [notes, setNotes] = useState('');
  const [price, setPrice] = useState('');
  const [listId, setListId] = useState(initialListId);
  const [categoryId, setCategoryId] = useState(initialCategoryId);

  const [lists, setLists] = useState([]);
  const [categories, setCategories] = useState([]);

  const [parsed, setParsed] = useState(null);
  const [parseLoading, setParseLoading] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [customTitle, setCustomTitle] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [duplicate, setDuplicate] = useState(null);

  const parseTimeoutRef = useRef(null);

  // Fetch lists on mount
  useEffect(() => {
    client.get('/lists')
      .then((res) => {
        setLists(res.data);
        if (!listId && res.data.length > 0) {
          setListId(res.data[0].id);
        }
      })
      .catch(console.error);
  }, []);

  // Fetch categories when list changes
  useEffect(() => {
    if (!listId) return;
    client.get(`/categories?list_id=${listId}`)
      .then((res) => {
        setCategories(res.data);
        if (!categoryId && res.data.length > 0) {
          setCategoryId(res.data[0].id);
        } else if (categoryId && !res.data.find((c) => c.id === categoryId)) {
          setCategoryId(res.data[0]?.id || '');
        }
      })
      .catch(console.error);
  }, [listId]);

  // Auto-parse URL after user stops typing
  useEffect(() => {
    if (!url.trim()) {
      setParsed(null);
      setParseError(null);
      return;
    }

    let validUrl;
    try {
      validUrl = new URL(url.trim());
    } catch {
      setParsed(null);
      setParseError(null);
      return;
    }

    clearTimeout(parseTimeoutRef.current);
    parseTimeoutRef.current = setTimeout(async () => {
      setParseLoading(true);
      setParseError(null);
      try {
        const response = await client.post('/parse', { url: url.trim() });
        setParsed(response.data);
      } catch (err) {
        setParseError(err.response?.data?.message || 'Не удалось загрузить превью');
        setParsed(null);
      } finally {
        setParseLoading(false);
      }
    }, 800);

    return () => clearTimeout(parseTimeoutRef.current);
  }, [url]);

  // Auto-parse if URL is pre-filled
  useEffect(() => {
    if (initialUrl) {
      // Trigger parse immediately
      const timeout = setTimeout(() => {
        client.post('/parse', { url: initialUrl })
          .then((res) => setParsed(res.data))
          .catch(() => {});
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, []);

  const handleUrlChange = (e) => {
    setUrl(e.target.value);
    setParsed(null);
    setDuplicate(null);
    setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!url.trim() && !customTitle.trim()) {
      setSubmitError('Укажи ссылку или название');
      return;
    }
    if (!categoryId) {
      setSubmitError('Выбери категорию');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setDuplicate(null);

    try {
      await client.post('/items', {
        category_id: categoryId,
        url: url.trim() || null,
        title: customTitle.trim() || parsed?.title || null,
        description: parsed?.description || null,
        image_url: parsed?.image_url || null,
        notes: notes.trim() || null,
        price: price.trim() || null,
      });

      setSubmitSuccess(true);

      // Navigate back after short delay
      setTimeout(() => {
        if (listId) {
          const dest = categoryId
            ? `/lists/${listId}?category_id=${categoryId}`
            : `/lists/${listId}`;
          navigate(dest);
        } else {
          navigate('/');
        }
      }, 1000);
    } catch (err) {
      if (err.response?.status === 409) {
        setDuplicate(err.response.data);
      } else {
        setSubmitError(err.response?.data?.error || 'Не удалось добавить желание');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selectedList = lists.find((l) => l.id === listId);
  const selectedCategory = categories.find((c) => c.id === categoryId);

  return (
    <Layout title="Добавить желание" showBack>
      <div style={styles.container}>
        {submitSuccess && (
          <div style={styles.successMsg}>
            ✅ Желание добавлено!
          </div>
        )}

        {duplicate && (
          <div style={styles.duplicateWarning}>
            ⚠️ Этот URL уже есть в категории.{' '}
            <button
              style={{ background: 'none', border: 'none', color: '#ff9f0a', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate(`/items/${duplicate.existing_id}`)}
            >
              Перейти к существующему
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Название *</label>
            <input
              style={styles.input}
              type="text"
              placeholder="Велосипед, собака, поездка в Японию..."
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              autoFocus={!initialUrl}
              maxLength={500}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Ссылка (необязательно)</label>
            <input
              style={styles.input}
              type="text"
              placeholder="https://example.com/product"
              value={url}
              onChange={handleUrlChange}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          {(parsed || parseLoading || parseError) && url && (
            <div style={styles.previewSection}>
              <label style={styles.label}>Превью</label>
              <ParsePreview
                parsed={parsed}
                loading={parseLoading}
                error={parseError}
                url={url}
              />
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Вишлист</label>
            <select
              style={styles.select}
              value={listId}
              onChange={(e) => {
                setListId(e.target.value);
                setCategoryId('');
              }}
              required
            >
              <option value="">Выбери список...</option>
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.title}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Категория</label>
            <select
              style={styles.select}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              disabled={!listId || categories.length === 0}
            >
              <option value="">Выбери категорию...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Цена (необязательно)</label>
            <input
              style={styles.input}
              type="text"
              placeholder="1 500 ₽"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              maxLength={100}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Заметка (по желанию)</label>
            <textarea
              style={styles.textarea}
              placeholder="Добавь заметку..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              maxLength={2000}
            />
          </div>

          {submitError && (
            <div style={styles.errorText}>{submitError}</div>
          )}

          <button
            type="submit"
            className="btn-primary"
            style={styles.submitBtn}
            disabled={submitting || (!url.trim() && !customTitle.trim()) || !categoryId}
          >
            {submitting ? 'Добавление...' : 'Добавить в вишлист'}
          </button>
        </form>
      </div>
    </Layout>
  );
}
